import {
  FAIcon,
  GenericLink,
  NextgenImage,
  Theme,
  useBreakpoint,
  IconStyle,
  LoaderType,
  getCdnAssetUrl,
} from '@nextgen-web-framework/all';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { ConsoleLogger } from '@nextgen-web-framework/core';
import styles from './ContentCard.module.scss';
import { Contents, ResourceType, type ContentCardProps, Icons } from './types/propsTypes';

const renderLineSeperator = () => <div className={styles['vertical-line']}></div>;
const renderDotSeperator = () => <div className={styles['dot']}></div>;

const renderLine = (seperator: JSX.Element | string, content: Array<string | undefined>) => (
  <div className={styles['footer-line']}>
    {content
      .filter((item) => Boolean(item))
      .map((item, index) => (
        <>
          {index > 0 && seperator} {item && renderValue(item)}
        </>
      ))}
  </div>
);

const renderValue = (value: string) => <div className={styles['value']}>{value}</div>;

const RenderFooter = ({
  type,
  time,
  date,
  firstAccountTypeDate,
  firstAccountTypeTitle,
  secondAccountTypeDate,
  secondAccountTypeTitle,
  size,
}: ContentCardProps) => {
  let element = <></>;
  switch (type) {
    case Contents.article:
      element = renderLine(renderLineSeperator(), [time, date]);
      break;
    case Contents.promotion:
      element = (
        <>
          {renderLine(renderDotSeperator(), [firstAccountTypeTitle, firstAccountTypeDate])}
          {renderLine(renderDotSeperator(), [secondAccountTypeTitle, secondAccountTypeDate])}{' '}
        </>
      );
      break;
    case Contents.resource:
      element = renderLine(renderLineSeperator(), [size, time, date]);
      break;
    default:
  }

  return element;
};

const ContentCard = (props: ContentCardProps) => {
  const viewPortType = useBreakpoint();
  const bannerImage = props?.imageList?.find((item) => item?.viewport?.includes(viewPortType));
  const bgColor = `card-bg-color-${props.backgroundColor || 'white'}`;
  const txtColor = `card-color-${props.color || 'black'}`;

  const getIconName = (): IconName => {
    let iconName = '';
    switch (props.resourceType) {
      case ResourceType.image:
        iconName = Icons.Image;
        break;
      case ResourceType.video:
        iconName = Icons.Video;
        break;
      case ResourceType.pdf:
        iconName = Icons.PDF;
        break;
      default:
    }
    return iconName as IconName;
  };

  const iconClick = () => {
    ConsoleLogger.info(`${props.type} view`);
  };

  const iconName = getIconName();

  return (
    <GenericLink
      href={props.resourceType === ResourceType.pdf ? getCdnAssetUrl(props.url ?? '') : (props.url ?? '')}
      className={styles['content-card-link']}
    >
      <div
        className={`${styles['content-card-container']}`}
        data-testid={`content-${props.type}-card-container-testid`}
      >
        <div className={styles['banner']}>
          {bannerImage?.url && (
            <NextgenImage
              src={bannerImage.url}
              alt={bannerImage?.altText ?? ''}
              className={styles['content-card-image']}
              loaderType={LoaderType.CONTENTSTACK}
              imageWidths={{
                small: 350,
                medium: 350,
                large: 480,
                xlarge: 480,
              }}
            />
          )}
          {iconName && props.type == Contents.resource && (
            <div
              className={styles['icon-container']}
              data-testid="content-type-icon-testid"
              role="presentation"
              onClick={iconClick}
              onKeyDown={iconClick}
            >
              <FAIcon iconName={iconName} theme={Theme.WHITE} iconStyle={IconStyle.SOLID} />
            </div>
          )}
        </div>
        <div className={`${styles['content']} ${styles[bgColor]} ${styles[txtColor]}`}>
          <div className={styles['header']}>
            <div className={styles['tag']}>{props.tag}</div>
            {props.title && (
              <div className={styles['title']} title={props.title}>
                {props.title}
              </div>
            )}
          </div>
          {props.description && <div className={styles['description']}>{props.description}</div>}
          <div className={styles['footer']}>{RenderFooter(props)}</div>
        </div>
      </div>
    </GenericLink>
  );
};

export default ContentCard;
export { ContentCardProps, Contents, ResourceType, RenderFooter };
